'use client';

import { useState } from 'react';
import type { GiftPlan, LsAddonPlan } from '@/lib/pricing';

// ─── Visual gift-card gradients (credit-card aspect ratio) ───────────────────
const CARD_GRADIENTS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
];

const LS_VIEWER_OPTIONS = [50, 100, 250, 500];
const LS_HOUR_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

interface Props {
  giftPlans: GiftPlan[];
  lsPlan: LsAddonPlan | null;
  currency: string;
}

function fmt(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

// ─── Single plan gift card ────────────────────────────────────────────────────
function PlanCard({
  plan,
  index,
  selected,
  onSelect,
  currency,
}: {
  plan: GiftPlan;
  index: number;
  selected: GiftPlan | null;
  onSelect: (p: GiftPlan) => void;
  currency: string;
}) {
  const isSelected = selected?.id === plan.id;
  const gradient = CARD_GRADIENTS[index % CARD_GRADIENTS.length];
  const eventsCount = plan.options?.events_count ?? null;
  const subtext = eventsCount ? `${eventsCount} event${eventsCount !== 1 ? 's' : ''}` : plan.frequency ?? '';

  return (
    <button
      type="button"
      onClick={() => onSelect(plan)}
      className="w-full text-left"
      style={{
        outline: isSelected ? '3px solid #FFC933' : '3px solid transparent',
        outlineOffset: 3,
        borderRadius: 16,
        border: 'none',
        padding: 0,
        background: 'transparent',
        transform: isSelected ? 'scale(1.03)' : 'scale(1)',
        transition: 'outline 0.15s, transform 0.15s',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          width: '100%',
          aspectRatio: '1.586 / 1',
          background: gradient,
          borderRadius: 14,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: isSelected
            ? '0 8px 28px rgba(0,0,0,0.28)'
            : '0 4px 14px rgba(0,0,0,0.15)',
          userSelect: 'none',
        }}
      >
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
        <div style={{ position: 'absolute', bottom: -50, left: -30, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

        {/* Logo */}
        <div style={{ position: 'absolute', top: 14, left: 16, fontSize: '0.85rem', fontWeight: 800, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          VibeVite
        </div>

        {/* Gift icon */}
        <div style={{ position: 'absolute', top: 10, right: 14, fontSize: 20 }}>🎁</div>

        {/* Plan name + subtext */}
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', color: '#fff', width: '80%' }}>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, letterSpacing: '0.03em', textShadow: '0 1px 4px rgba(0,0,0,0.25)' }}>
            {plan.name}
          </div>
          {subtext && (
            <div style={{ fontSize: '0.7rem', opacity: 0.85, marginTop: 2 }}>{subtext}</div>
          )}
        </div>

        {/* Price */}
        <div style={{ position: 'absolute', bottom: 14, right: 16, color: '#fff', fontWeight: 800, fontSize: '1.3rem', textShadow: '0 1px 4px rgba(0,0,0,0.3)', letterSpacing: '0.02em' }}>
          {fmt(plan.price, currency)}
        </div>

        {/* Chip placeholder */}
        <div style={{ position: 'absolute', bottom: 14, left: 16, width: 30, height: 22, borderRadius: 4, background: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.4)' }} />

        {/* Selected checkmark */}
        {isSelected && (
          <div style={{ position: 'absolute', top: 10, right: 46, width: 20, height: 20, borderRadius: '50%', backgroundColor: '#FFC933', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#000', boxShadow: '0 2px 6px rgba(0,0,0,0.25)' }}>
            ✓
          </div>
        )}
      </div>
    </button>
  );
}

// ─── Live-streaming preview card (static, updates live) ─────────────────────
function LiveStreamCard({
  lsPlan,
  enabled,
  viewers,
  hours,
  lsTotal,
  currency,
}: {
  lsPlan: LsAddonPlan;
  enabled: boolean;
  viewers: number;
  hours: number;
  lsTotal: number;
  currency: string;
}) {
  return (
    <div
      style={{
        width: '100%',
        aspectRatio: '1.586 / 1',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        borderRadius: 14,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: enabled ? '0 8px 28px rgba(0,0,0,0.35)' : '0 4px 14px rgba(0,0,0,0.2)',
        opacity: enabled ? 1 : 0.45,
        transition: 'opacity 0.2s, box-shadow 0.2s',
        userSelect: 'none',
      }}
    >
      {/* Decorative circles */}
      <div style={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
      <div style={{ position: 'absolute', bottom: -40, left: -20, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

      {/* Signal bars top-right */}
      <div style={{ position: 'absolute', top: 10, right: 12, display: 'flex', gap: 2, alignItems: 'flex-end' }}>
        {([8, 12, 16, 20] as number[]).map((h, i) => (
          <div key={i} style={{ width: 3, height: h, borderRadius: 2, backgroundColor: enabled ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)' }} />
        ))}
      </div>

      {/* Logo */}
      <div style={{ position: 'absolute', top: 16, left: 18, fontSize: '0.8rem', fontWeight: 800, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>VibeVite</div>

      {/* Centre */}
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', color: '#fff', width: '80%' }}>
        <div style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.15em', opacity: 0.7, marginBottom: 6, textTransform: 'uppercase' }}>Live Streaming Gift</div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.68rem', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '2px 9px', fontWeight: 600 }}>👥 {viewers} viewers</span>
          <span style={{ fontSize: '0.68rem', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '2px 9px', fontWeight: 600 }}>⏱ {hours}h</span>
        </div>
      </div>

      {/* Dynamic price bottom-right */}
      <div style={{ position: 'absolute', bottom: 14, right: 18, color: '#fff', fontWeight: 800, fontSize: '1.25rem', textShadow: '0 1px 4px rgba(0,0,0,0.4)', letterSpacing: '0.02em' }}>
        {enabled ? fmt(lsTotal, currency) : '—'}
      </div>

      {/* LIVE dot + label bottom-left */}
      <div style={{ position: 'absolute', bottom: 18, left: 18, display: 'flex', alignItems: 'center', gap: 5 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: enabled ? '#ff1744' : '#888', boxShadow: enabled ? '0 0 6px #ff1744' : 'none' }} />
        <span style={{ fontSize: '0.6rem', color: '#fff', fontWeight: 700, letterSpacing: 1 }}>LIVE</span>
      </div>

      {/* Chip */}
      <div style={{ position: 'absolute', bottom: 14, left: 58, width: 32, height: 24, borderRadius: 4, background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)' }} />
    </div>
  );
}

// ─── Main form component ──────────────────────────────────────────────────────
export default function GiftPurchaseForm({ giftPlans, lsPlan, currency }: Props) {
  const [selectedPlan, setSelectedPlan] = useState<GiftPlan | null>(
    giftPlans.length > 0 ? giftPlans[0] : null
  );
  const [lsEnabled, setLsEnabled] = useState(false);
  const [lsViewers, setLsViewers] = useState(50);
  const [lsHours, setLsHours] = useState(2);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    gifter_name: '',
    gifter_email: '',
    recipient_name: '',
    recipient_email: '',
    personal_message: '',
  });

  const lsCostCents = lsEnabled && lsPlan ? lsPlan.unit_amount * lsViewers * lsHours : 0;
  const lsCostDollars = lsCostCents / 100;
  const totalDollars = (selectedPlan?.price ?? 0) + lsCostDollars;

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!selectedPlan) {
      setError('Please select a plan');
      return;
    }
    if (!form.gifter_name.trim()) {
      setError('Your name is required');
      return;
    }
    if (!form.gifter_email.trim()) {
      setError('Your email is required');
      return;
    }
    if (!form.recipient_name.trim()) {
      setError('Recipient name is required');
      return;
    }
    if (!form.recipient_email.trim()) {
      setError('Recipient email is required');
      return;
    }

    try {
      setSubmitting(true);
      const payload: Record<string, any> = {
        plan_id: selectedPlan.id,
        gifter_name: form.gifter_name,
        gifter_email: form.gifter_email,
        recipient_name: form.recipient_name,
        recipient_email: form.recipient_email,
        personal_message: form.personal_message,
      };

      if (lsEnabled && lsPlan && lsPlan.stripe_price_id) {
        payload.ls_stripe_price_id = lsPlan.stripe_price_id;
        payload.ls_quantity = lsViewers * lsHours;
        payload.ls_viewers = lsViewers;
        payload.ls_hours = lsHours;
      }

      const res = await fetch('/api/gift/purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success && data.data?.checkout_url) {
        window.location.href = data.data.checkout_url;
      } else {
        setError(data.message || 'Failed to initiate checkout. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* ── Step 1: Choose a plan ── */}
      <div className="mb-8">
        <h2 className="mb-5 flex items-center gap-3 text-lg font-bold text-[#2B0A4A]">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2B0A4A] text-sm font-bold text-[#FFC933]">1</span>
          Choose a Plan
        </h2>

        {giftPlans.length === 0 ? (
          <p className="rounded-xl bg-[#FFF8EC] p-6 text-center text-[#1B0044]/70">
            No gift plans available at the moment. Please try again later.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {giftPlans.map((plan, i) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                index={i}
                selected={selectedPlan}
                onSelect={setSelectedPlan}
                currency={currency}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Live Streaming Add-on ── */}
      {lsPlan && (
        <div className="mb-8">
          <div className="mb-4 flex items-center gap-2">
            <h2 className="flex items-center gap-3 text-lg font-bold text-[#2B0A4A]">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2B0A4A] text-sm font-bold text-[#FFC933]">2</span>
              Add Live Streaming
            </h2>
            <span className="rounded-full px-2 py-0.5 text-xs font-semibold" style={{ backgroundColor: '#e8f4fd', color: '#1565c0' }}>Optional</span>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-6 md:grid md:items-center md:[grid-template-columns:5fr_7fr]">
              {/* Col 1 — live preview card */}
              <div className="w-full">
                <LiveStreamCard
                  lsPlan={lsPlan}
                  enabled={lsEnabled}
                  viewers={lsViewers}
                  hours={lsHours}
                  lsTotal={lsCostDollars}
                  currency={currency}
                />
              </div>

              {/* Col 2 — toggle + controls */}
              <div className="w-full min-w-0">
                {/* Toggle row */}
                <div className="mb-4 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setLsEnabled((v) => !v)}
                    style={{
                      width: 42,
                      height: 24,
                      borderRadius: 12,
                      backgroundColor: lsEnabled ? '#0f3460' : '#ced4da',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      flexShrink: 0,
                      border: 'none',
                      padding: 0,
                    }}
                    aria-pressed={lsEnabled}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 3,
                      left: lsEnabled ? 20 : 3,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }} />
                  </button>
                  <span className="text-sm font-semibold text-[#2B0A4A]">
                    {lsEnabled ? 'Live Streaming included' : 'Add Live Streaming'}
                  </span>
                </div>

                {lsEnabled ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="min-w-0">
                      <label className="mb-1 block text-xs font-bold text-[#2B0A4A]">Audience size</label>
                      <select
                        value={lsViewers}
                        onChange={(e) => setLsViewers(parseInt(e.target.value))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0f3460] focus:outline-none focus:ring-2 focus:ring-[#0f3460]/20"
                      >
                        {LS_VIEWER_OPTIONS.map((v) => (
                          <option key={v} value={v}>{v} viewers</option>
                        ))}
                      </select>
                    </div>
                    <div className="min-w-0">
                      <label className="mb-1 block text-xs font-bold text-[#2B0A4A]">Duration</label>
                      <select
                        value={lsHours}
                        onChange={(e) => setLsHours(parseInt(e.target.value))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0f3460] focus:outline-none focus:ring-2 focus:ring-[#0f3460]/20"
                      >
                        {LS_HOUR_OPTIONS.map((h) => (
                          <option key={h} value={h}>{h} {h === 1 ? 'hour' : 'hours'}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Gift a live streaming session so the recipient can broadcast their event to remote guests. Price is calculated per viewer per hour.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Order summary ── */}
      {selectedPlan && (
        <div className="mb-8 rounded-xl border border-yellow-200 bg-[#FFFBEC] px-4 py-3 text-sm">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>🎟️ <strong>{selectedPlan.name}</strong> — {fmt(selectedPlan.price, currency)}</span>
            {lsEnabled && lsPlan && (
              <span>📡 <strong>Live Streaming</strong> ({lsViewers} viewers × {lsHours}h) — {fmt(lsCostDollars, currency)}</span>
            )}
            <span className="ml-auto font-bold text-amber-700">
              Total: {fmt(totalDollars, currency)}
            </span>
          </div>
        </div>
      )}

      {/* ── Step 3: Your details ── */}
      <div className="mb-8">
        <h2 className="mb-5 flex items-center gap-3 text-lg font-bold text-[#2B0A4A]">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2B0A4A] text-sm font-bold text-[#FFC933]">3</span>
          Your Details
        </h2>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="gifter_name" className="mb-1 block text-sm font-semibold text-[#2B0A4A]">
                Your Name <span className="text-red-500">*</span>
              </label>
              <input
                id="gifter_name"
                type="text"
                name="gifter_name"
                value={form.gifter_name}
                onChange={handleInputChange}
                placeholder="Enter your name"
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm transition-colors focus:border-[#FFC933] focus:outline-none focus:ring-2 focus:ring-[#FFC933]/30"
              />
            </div>
            <div>
              <label htmlFor="gifter_email" className="mb-1 block text-sm font-semibold text-[#2B0A4A]">
                Your Email <span className="text-red-500">*</span>
              </label>
              <input
                id="gifter_email"
                type="email"
                name="gifter_email"
                value={form.gifter_email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm transition-colors focus:border-[#FFC933] focus:outline-none focus:ring-2 focus:ring-[#FFC933]/30"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Step 4: Recipient details ── */}
      <div className="mb-8">
        <h2 className="mb-5 flex items-center gap-3 text-lg font-bold text-[#2B0A4A]">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2B0A4A] text-sm font-bold text-[#FFC933]">4</span>
          Recipient Details
        </h2>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="recipient_name" className="mb-1 block text-sm font-semibold text-[#2B0A4A]">
                Recipient Name <span className="text-red-500">*</span>
              </label>
              <input
                id="recipient_name"
                type="text"
                name="recipient_name"
                value={form.recipient_name}
                onChange={handleInputChange}
                placeholder="Enter their name"
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm transition-colors focus:border-[#FFC933] focus:outline-none focus:ring-2 focus:ring-[#FFC933]/30"
              />
            </div>
            <div>
              <label htmlFor="recipient_email" className="mb-1 block text-sm font-semibold text-[#2B0A4A]">
                Recipient Email <span className="text-red-500">*</span>
              </label>
              <input
                id="recipient_email"
                type="email"
                name="recipient_email"
                value={form.recipient_email}
                onChange={handleInputChange}
                placeholder="Enter their email"
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm transition-colors focus:border-[#FFC933] focus:outline-none focus:ring-2 focus:ring-[#FFC933]/30"
              />
            </div>
          </div>

          <div>
            <label htmlFor="personal_message" className="mb-1 block text-sm font-semibold text-[#2B0A4A]">
              Personal Message <span className="text-[#1B0044]/50">(optional)</span>
            </label>
            <textarea
              id="personal_message"
              name="personal_message"
              value={form.personal_message}
              onChange={handleInputChange}
              placeholder="Add a personal note to the recipient…"
              rows={3}
              maxLength={500}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm transition-colors focus:border-[#FFC933] focus:outline-none focus:ring-2 focus:ring-[#FFC933]/30"
            />
            <p className="mt-1 text-right text-xs text-[#1B0044]/50">
              {form.personal_message.length}/500
            </p>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <svg className="mt-0.5 h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      {/* Submit */}
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
        <a
          href="/gift"
          className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[#2B0A4A]/30 px-6 py-3 text-sm font-semibold text-[#2B0A4A] transition-all hover:border-[#2B0A4A] hover:bg-[#2B0A4A]/5"
        >
          ← Back
        </a>
        <button
          type="submit"
          disabled={submitting || !selectedPlan}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#2B0A4A] to-[#1B0044] px-8 py-3 text-base font-semibold text-white shadow-lg transition-all hover:scale-105 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <>
              <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Processing…
            </>
          ) : selectedPlan ? (
            `Proceed to Payment · ${fmt(totalDollars, currency)}`
          ) : (
            'Proceed to Payment →'
          )}
        </button>
      </div>
    </form>
  );
}
